/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { TypeDTCChart } from './TypeChart';
import { DailyDTCChart } from './DailyChart';
import { cleanDTCString } from './utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

export const DTCChart = ({
  enableDarkTheme,
  data,
  selectedAsset,
  setSelectedAsset,
  selectedType,
  setSelectedType,
}: {
  enableDarkTheme: boolean;
  data: any[];
  selectedAsset: string | null;
  setSelectedAsset: any;
  selectedType: string | null;
  setSelectedType: any;
}) => {
  const { t } = useTranslation();
  const [colorMap, setColorMap] = useState<Record<string, string>>({});
  const [highlightedBar, setHighlightedBar] = useState(null);
  const [maxItems, setMaxItems] = useState(50);

  const colors = [
    '#4AD8C9',
    '#8ac2f2',
    '#42C3FF',
    '#2167B2',
    '#D6C29F',
    '#A8B2B6',
    '#F6E58D',
    '#D9A92E',
    '#3D606E',
    '#6ADBC7',
    '#A1D5F3',
    '#88A8C9',
    '#C5B59D',
    '#B7C6C3',
    '#F2D58D',
    '#C3AA67',
    '#7B98A5',
    '#5C7D89',
  ];

  const handleBarClick = (data: any) => {
    setHighlightedBar(data.name);
  };

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  useEffect(() => {
    if (!filteredData || filteredData.length === 0) return;

    const uniqueDtcTypes = [
      ...new Set(filteredData.map((item) => item.dtc_type)),
    ];
    const uniqueDtcCodes = [
      ...new Set(filteredData.map((item) => item.dtc_code)),
    ];

    const colorMapping: Record<string, string> = {};

    uniqueDtcTypes.forEach((type, index) => {
      colorMapping[type as string] = colors[index % colors.length];
    });

    uniqueDtcCodes.forEach((type, index) => {
      colorMapping[type as string] = colors[index % colors.length];
    });

    setColorMap(colorMapping);
  }, [filteredData]);

  return (
    <Box
      sx={{
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography
          sx={{
            color: (enableDarkTheme ? _palette.dark : _palette.light)
              .textPrimary,
          }}
        >
          {t('date_wise_distribution_of_sensor_type')}
          {filteredData.length > 0 && (
            <span
              style={{ fontSize: '12px', marginLeft: '10px', opacity: 0.7 }}
            >
              ({filteredData.length} {t('total_records')}
            </span>
          )}
        </Typography>

        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              marginBottom: '10px',
            }}
          >
            <Typography
              sx={{
                color: (enableDarkTheme ? _palette.dark : _palette.light)
                  .textPrimary,
                fontSize: '12px',
                marginTop: '8px',
              }}
            >
              {selectedAsset ? (
                <>
                  {t('dtc_showing_prefix')}{' '}
                  {selectedType
                    ? cleanDTCString(selectedType)
                    : t('dtc_all_types')}{' '}
                  {t('distribution_for_asset')}{' '}
                  <span
                    style={{
                      fontSize: '14px',
                      color: enableDarkTheme
                        ? _palette.dark.primary
                        : _palette.light.primary,
                    }}
                  >
                    {selectedAsset}
                  </span>
                </>
              ) : (
                t('dtc_click_row_to_view_distribution')
              )}
            </Typography>

            {selectedAsset && (
              <Button
                sx={{
                  color: '#FFFFFF',
                  border: `1px solid ${_palette.dark.borderGrey}`,
                  backgroundColor: enableDarkTheme
                    ? _palette.dark.primary
                    : _palette.light.primary,
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? _palette.dark.primaryDark
                      : _palette.light.primaryDark,
                  },
                  fontSize: '12px',
                  textTransform: 'none',
                }}
                onClick={() => {
                  setSelectedAsset(null);
                  setSelectedType(null);
                  setHighlightedBar(null);
                }}
              >
                Reset
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel
                sx={{
                  fontSize: '12px',
                  color: (enableDarkTheme ? _palette.dark : _palette.light)
                    .textSecondary,
                  '&.Mui-focused': {
                    color: enableDarkTheme
                      ? (enableDarkTheme ? _palette.dark : _palette.light)
                          .primary
                      : ThemePalette.typography.primary,
                  },
                }}
              >
                {t('max_items')}
              </InputLabel>
              <Select
                value={maxItems}
                onChange={(e) => {
                  setMaxItems(Number(e.target.value));
                }}
                label={t('max_items')}
                sx={{
                  color: (enableDarkTheme ? _palette.dark : _palette.light)
                    .textSecondary,
                  ...(enableDarkTheme && {
                    '& .MuiSvgIcon-root': {
                      color: 'purple', // <------------------ arrow-svg-color
                    },
                    '&.MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: (enableDarkTheme
                          ? _palette.dark
                          : _palette.light
                        ).borderGrey, // <------------------ outline-color by default
                      },
                      '&:hover fieldset': {
                        borderColor: (enableDarkTheme
                          ? _palette.dark
                          : _palette.light
                        ).primary, // <------------------ outline-color on hover
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (enableDarkTheme
                          ? _palette.dark
                          : _palette.light
                        ).primary, // <------------------ outline-color on focus
                      },
                    },
                  }),
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: enableDarkTheme
                        ? (enableDarkTheme ? _palette.dark : _palette.light)
                            .menuOptions
                        : (enableDarkTheme ? _palette.dark : _palette.light)
                            .boxBackground,
                    },
                  },
                }}
              >
                {[25, 50, 100, 200].map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      value={item}
                      sx={{
                        color: (enableDarkTheme
                          ? _palette.dark
                          : _palette.light
                        ).textSecondary,
                      }}
                    >
                      Top {item}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <TypeDTCChart
          enableDarkTheme={enableDarkTheme}
          data={filteredData}
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          highlightedBar={highlightedBar}
          handleBarClick={handleBarClick}
          colorMap={colorMap}
          maxItems={maxItems}
        />

        <DailyDTCChart
          enableDarkTheme={enableDarkTheme}
          data={filteredData}
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          colorMap={colorMap}
          maxItems={maxItems}
        />
      </Grid>
    </Box>
  );
};
